import { tagular } from "@redventures/cohesion-utils-react";

import decodeAndStringifyValue from "@/lib/decode-and-stringify-value";
import { TAGULAR_EVENT_TYPES } from "@/types/tagular";

type Salesflow = "CALL" | "CART" | "SITE";

type IntegrationContext = {
  brand?: string;
  callId?: string;
  clientId?: string;
  clientSessionIDEventName?: string;
  clientSessionIDKey?: string;
  clientSessionId?: string;
  orderId?: string;
  partner?: string;
  processId?: string;
  salesflow?: Salesflow;
  senderId?: string;
  siteSessionId?: string;
};

export type AddressCapturedOptions = {
  activityDateTime?: string;
  address?: string;
  addressId?: string;
  integrationContext?: IntegrationContext;
  postalCode?: string;
  zip4?: number;
};

/**
 * Utility: fireAddressCaptured
 *  * @see {@link https://app.make.rvapps.io/schemas/sch_3DPb28UwXKY7a3CLVxeWF3oEQVk | AddressCaptured Schema}
 */
const fireAddressCaptured = (
  eventOptions: AddressCapturedOptions,
  callback?: () => void
) => {
  const {
    activityDateTime = "",
    address = "",
    addressId = "",
    integrationContext,
    postalCode = "",
    zip4,
  } = eventOptions;

  if (!address && !postalCode && zip4 === undefined && !addressId) return;

  const payload: Record<string, unknown> = {
    "@type": TAGULAR_EVENT_TYPES.ADDRESS_CAPTURED,
    activityDateTime: decodeAndStringifyValue(
      activityDateTime || new Date().toISOString()
    ),
    ...(address && {
      address: decodeAndStringifyValue(address),
    }),
    ...(addressId && {
      addressId: decodeAndStringifyValue(addressId),
    }),
    ...(integrationContext && {
      integrationContext: {
        ...(integrationContext.brand && {
          brand: decodeAndStringifyValue(integrationContext.brand),
        }),
        ...(integrationContext.callId && {
          callId: decodeAndStringifyValue(integrationContext.callId),
        }),
        ...(integrationContext.clientId && {
          clientId: decodeAndStringifyValue(integrationContext.clientId),
        }),
        ...(integrationContext.clientSessionIDEventName && {
          clientSessionIDEventName: decodeAndStringifyValue(
            integrationContext.clientSessionIDEventName
          ),
        }),
        ...(integrationContext.clientSessionIDKey && {
          clientSessionIDKey: decodeAndStringifyValue(
            integrationContext.clientSessionIDKey
          ),
        }),
        ...(integrationContext.clientSessionId && {
          clientSessionId: decodeAndStringifyValue(
            integrationContext.clientSessionId
          ),
        }),
        ...(integrationContext.orderId && {
          orderId: decodeAndStringifyValue(integrationContext.orderId),
        }),
        ...(integrationContext.partner && {
          partner: decodeAndStringifyValue(integrationContext.partner),
        }),
        ...(integrationContext.processId && {
          processId: decodeAndStringifyValue(integrationContext.processId),
        }),
        ...(integrationContext.salesflow && {
          salesflow: decodeAndStringifyValue(integrationContext.salesflow),
        }),
        ...(integrationContext.senderId && {
          senderId: decodeAndStringifyValue(integrationContext.senderId),
        }),
        ...(integrationContext.siteSessionId && {
          siteSessionId: decodeAndStringifyValue(
            integrationContext.siteSessionId
          ),
        }),
      },
    }),
    ...(postalCode && {
      postalCode: decodeAndStringifyValue(postalCode),
    }),
    ...(zip4 !== undefined && {
      zip4,
    }),
  };

  tagular("beam", payload);

  if (typeof callback === "function") {
    callback?.();
  }
};

export default fireAddressCaptured;
